// src/components/issues/BrouillonsNcs.tsx — SCE Issues
// « NC-S en cours » visible PARTOUT (06/10, demande de Guillaume) : une NC-S
// réduite (brouillon du navigateur, utils/brouillonNcs) se reprend depuis
// n'importe quelle page. Une pastille en bas à droite ; plusieurs brouillons :
// la pastille ouvre leur liste. Un clic ouvre la fiche du problème
// directement sur le formulaire NC-S (`state.ouvrirNcs`).
//
// Monté une fois (App.tsx). Masqué tant qu'une fenêtre NC-S est ouverte, et
// pour un fournisseur.

import { useEffect, useState, type JSX } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ChevronUp } from 'lucide-react'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useEstBureau } from '@/hooks/useMediaQuery'
import { lienProbleme } from '@/utils/liensFiches'
import { EVENEMENT_BROUILLONS, fenetreNcsOuverte, listerBrouillonsNcs, type Brouillon } from '@/utils/brouillonNcs'
import type { EtatModale } from '@/components/layout/ModaleRoute'

type Liste = { issueId: string; brouillon: Brouillon }[]

/** Le composant saisi, sinon le NVS, sinon rien. */
function composantDe(b: Brouillon): string {
  const d = b.saisie.designationComposant.trim()
  const nvs = b.saisie.nvs.trim()
  return d || (nvs.toUpperCase() !== 'NVS' ? nvs : '')
}

export function BrouillonsNcs(): JSX.Element | null {
  const { isFournisseur, user } = useCurrentUser()
  const navigate = useNavigate()
  const location = useLocation()
  const estBureau = useEstBureau()
  const [brouillons, setBrouillons] = useState<Liste>(() => listerBrouillonsNcs())
  const [masque, setMasque] = useState(() => fenetreNcsOuverte())
  const [listeOuverte, setListeOuverte] = useState(false)

  useEffect(() => {
    const relire = (): void => {
      setBrouillons(listerBrouillonsNcs())
      setMasque(fenetreNcsOuverte())
    }
    window.addEventListener(EVENEMENT_BROUILLONS, relire)
    // Un autre onglet a écrit ou effacé un brouillon.
    window.addEventListener('storage', relire)
    return () => {
      window.removeEventListener(EVENEMENT_BROUILLONS, relire)
      window.removeEventListener('storage', relire)
    }
  }, [])

  // Changement de page : la liste se replie.
  useEffect(() => setListeOuverte(false), [location.pathname])

  if (!user || isFournisseur || masque || brouillons.length === 0) return null

  const reprendre = (issueId: string): void => {
    setListeOuverte(false)
    // Au bureau, la fiche s'ouvre par-dessus la page en cours (comme depuis une liste).
    const etatCourant = location.state as EtatModale | null
    const etat: EtatModale = { ouvrirNcs: true, ...(estBureau ? { fond: etatCourant?.fond ?? location } : {}) }
    navigate(lienProbleme(issueId), { state: etat })
  }

  const [premier] = brouillons
  const plusieurs = brouillons.length > 1

  return (
    <div className="ncs-brouillons">
      {listeOuverte && (
        <ul className="ncs-brouillons-liste" role="menu" aria-label="NC-S en cours">
          {brouillons.map(({ issueId, brouillon }) => (
            <li key={issueId}>
              <button type="button" role="menuitem" className="ncs-brouillons-ligne" onClick={() => reprendre(issueId)}>
                <strong>{issueId}</strong>
                <span>{composantDe(brouillon) || 'composant à saisir'}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        className="ncs-reprendre"
        onClick={() => (plusieurs ? setListeOuverte((o) => !o) : premier && reprendre(premier.issueId))}
        aria-expanded={plusieurs ? listeOuverte : undefined}
        title={plusieurs ? 'Choisir la NC-S à reprendre' : 'Reprendre la NC-S commencée'}
      >
        <span className="ncs-reprendre-point" aria-hidden="true" />
        {plusieurs ? (
          <>
            <strong style={{ fontWeight: 600 }}>{brouillons.length} NC-S en cours</strong>
            <ChevronUp size={16} style={{ transform: listeOuverte ? 'none' : 'rotate(180deg)', transition: 'transform 0.2s' }} />
          </>
        ) : (
          premier && (
            <>
              <span>
                <strong style={{ fontWeight: 600 }}>NC-S en cours</strong> · {premier.issueId}
                {composantDe(premier.brouillon) ? ` · ${composantDe(premier.brouillon)}` : ''}
              </span>
              <span style={{ textDecoration: 'underline' }}>Reprendre</span>
            </>
          )
        )}
      </button>
    </div>
  )
}
